// /opt/isp_erp/frontend/src/pages/admin/finanzas/promesas/PromesaDetalle.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { promesasApi } from '../../../../api/finanzas/promesas.api';
import { format } from 'date-fns';
import './PromesaDetalle.css';

export default function PromesaDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [promesa, setPromesa] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    cargarPromesa();
  }, [id]);

  const cargarPromesa = async () => {
    try {
      const response = await promesasApi.obtener(id);
      setPromesa(response.data);
    } catch (error) {
      console.error('Error cargando promesa:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnular = async () => {
    if (!window.confirm('¿Está seguro de anular esta promesa?')) return;

    const motivo = prompt('Motivo de anulación:');
    if (!motivo) return;

    try {
      await promesasApi.anular(id, motivo);
      alert('✅ Promesa anulada');
      cargarPromesa();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al anular');
    }
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return 'N/A';
    try {
      return format(new Date(fecha), 'dd/MM/yyyy');
    } catch {
      return 'Fecha inválida';
    }
  };

  if (loading) {
    return (
      <div className="promesa-loading-container">
        <div className="promesa-loading-spinner"></div>
        <p>Cargando detalles de la promesa...</p>
      </div>
    );
  }

  if (!promesa) {
    return (
      <div className="promesa-error-container">
        <div className="promesa-error-card">
          <div className="error-icon">❌</div>
          <h3>Promesa no encontrada</h3>
          <p>La promesa que buscas no existe o ha sido eliminada</p>
          <button
            onClick={() => navigate('/finanzas/promesas')}
            className="promesa-btn-primary"
          >
            ← Volver
          </button>
        </div>
      </div>
    );
  }

  const puedeAnular = ['PENDIENTE', 'PARCIAL', 'INCUMPLIDA'].includes(promesa.estado);

  return (
    <div className="promesa-detalle">
      {/* Header */}
      <div className="promesa-header">
        <div className="promesa-header-left">
          <div className="promesa-header-icon">📅</div>
          <div>
            <h1>Promesa de Pago {promesa.numero_promesa}</h1>
            <div className="promesa-cliente-info">
              <span className="cliente-nombre">{promesa.cliente_nombre}</span>
            </div>
          </div>
        </div>
        <div className="promesa-header-actions">
          {puedeAnular && (
            <button
              onClick={handleAnular}
              className="promesa-btn-anular"
              title="Anular promesa"
            >
              ❌ Anular
            </button>
          )}
          <button
            onClick={() => navigate('/finanzas/promesas')}
            className="promesa-btn-back"
          >
            ← Volver
          </button>
        </div>
      </div>

      {/* Badge de estado */}
      <div className="promesa-status">
        <span className={`status-badge status-${promesa.estado?.toLowerCase()}`}>
          {promesa.estado}
        </span>
      </div>

      {/* Resumen financiero */}
      <div className="promesa-resumen">
        <div className="resumen-card resumen-total">
          <div className="resumen-icon">💰</div>
          <div>
            <p className="resumen-label">Monto Total</p>
            <p className="resumen-value">${promesa.monto_total?.toFixed(2)}</p>
          </div>
        </div>

        <div className="resumen-card resumen-pagado">
          <div className="resumen-icon">✅</div>
          <div>
            <p className="resumen-label">Pagado</p>
            <p className="resumen-value">${promesa.monto_pagado?.toFixed(2)}</p>
          </div>
        </div>

        <div className="resumen-card resumen-saldo">
          <div className="resumen-icon">⏳</div>
          <div>
            <p className="resumen-label">Saldo Pendiente</p>
            <p className="resumen-value">${promesa.saldo_pendiente?.toFixed(2)}</p>
          </div>
        </div>

        <div className="resumen-card resumen-vencimiento">
          <div className="resumen-icon">📅</div>
          <div>
            <p className="resumen-label">Vencimiento</p>
            <p className="resumen-value">{formatearFecha(promesa.fecha_limite)}</p>
          </div>
        </div>
      </div>

      {/* Información adicional */}
      <div className="promesa-info-grid">
        <div className="info-card">
          <div className="info-icon">📆</div>
          <div>
            <p className="info-label">Fecha de Acuerdo</p>
            <p className="info-value">{formatearFecha(promesa.fecha_acuerdo)}</p>
          </div>
        </div>

        <div className="info-card">
          <div className="info-icon">💳</div>
          <div>
            <p className="info-label">Último Pago</p>
            <p className="info-value">{formatearFecha(promesa.fecha_ultimo_pago) || 'No hay pagos'}</p>
          </div>
        </div>
      </div>

      {/* Cuotas */}
      {promesa.cuotas && promesa.cuotas.length > 0 && (
        <div className="promesa-cuotas">
          <h2>Cuotas de la Promesa</h2>
          <div className="cuotas-table-container">
            <table className="cuotas-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Vencimiento</th>
                  <th className="text-right">Monto</th>
                  <th className="text-right">Pagado</th>
                  <th className="text-center">Estado</th>
                </tr>
              </thead>
              <tbody>
                {promesa.cuotas.map((cuota, index) => (
                  <tr key={cuota.id} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                    <td className="cuota-numero">{cuota.numero_cuota}</td>
                    <td className="cuota-fecha">{formatearFecha(cuota.fecha_vencimiento)}</td>
                    <td className="cuota-monto text-right">${cuota.monto?.toFixed(2)}</td>
                    <td className="cuota-pagado text-right">${(cuota.monto_pagado || 0).toFixed(2)}</td>
                    <td className="cuota-estado text-center">
                      {cuota.pagada ? (
                        <span className="estado-cuota pagada">
                          ✓ Pagada
                        </span>
                      ) : (
                        <span className="estado-cuota pendiente">
                          ⏳ Pendiente
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Descripción y condiciones */}
      {(promesa.descripcion || promesa.condiciones) && (
        <div className="promesa-condiciones">
          {promesa.descripcion && (
            <div className="condicion-item">
              <p className="condicion-label">Descripción</p>
              <p className="condicion-texto">{promesa.descripcion}</p>
            </div>
          )}
          {promesa.condiciones && (
            <div className="condicion-item">
              <p className="condicion-label">Condiciones</p>
              <p className="condicion-texto">{promesa.condiciones}</p>
            </div>
          )}
        </div>
      )}

      {/* Nota informativa */}
      <div className="promesa-nota">
        <div className="nota-icon">💡</div>
        <div className="nota-texto">
          Los pagos de esta promesa deben realizarse desde el módulo <strong>Finanzas → Pagos</strong>
        </div>
      </div>
    </div>
  );
}
